import { upperFirst } from 'lodash';
import React, { useMemo } from 'react';

import { getMosaicProps, isPromo } from '@swf/casino';
import type { CasinoGameTileProps, MosaicLayout } from '@swf/casino';
import type { CasinoGame, GameLike, Promo } from '@swf/core/types/CasinoGame';
import type { CasinoGameList } from '@swf/core/types/CasinoGameList';
import { isGame, isLive, tilesPerRowFromIcon } from '@swf/core/utils/casino';

import CasinoGameTile from '../CasinoGameTile/CasinoGameTile';
import CasinoGameTileSL from '../CasinoGameTileSL/CasinoGameTileSL';
import CasinoPromoTile from '../CasinoPromoTile/CasinoPromoTile';
import LiveCasinoGameTile from '../LiveCasinoGameTile/LiveCasinoGameTile';
import NumberedGameTile from '../NumberedGameTile/NumberedGameTile';

export interface Props
  extends Pick<
    CasinoGameList,
    | 'favouriteIcon'
    | 'fsbLaunch'
    | 'numbered'
    | 'onLaunch'
    | 'preload'
    | 'race'
    | 'renderInfo'
    | 'variant'
    | 'icon'
    | 'tilesPerRow'
  > {
  game: GameLike;
  count: number;
  index: number;
  slide: boolean;
  mosaic?: MosaicLayout;
  gameDeckMosaic?: boolean;
  jackpotClassName?: CasinoGameTileProps['jackpotClassName'];
  styles?: { [id: string]: string };
  gameCategory?: string;
  overlayHierarchy?: string;
  isNewSLView: boolean;
  disableIcons?: boolean;
  disableProviderLogos?: boolean;
  gameDeck?: boolean;
  jackpotWidget?: boolean;
}
export interface GameBasedProps
  extends Props,
    Pick<CasinoGameTileProps, 'noIconScale' | 'className' | 'mosaicHint'> {
  game: CasinoGame;
  styles: { [id: string]: string };
}

const GameBasedTile: React.FC<GameBasedProps> = ({
  game,
  index,
  styles,
  favouriteIcon,
  fsbLaunch,
  count,
  numbered,
  onLaunch,
  race,
  renderInfo,
  variant,
  slide,
  mosaic,
  noIconScale,
  className,
  mosaicHint,
  icon,
  jackpotClassName,
  isNewSLView,
  disableIcons,
  disableProviderLogos,
  gameDeck,
  ...rest
}) => {
  const tilesPerRow = rest.tilesPerRow || tilesPerRowFromIcon(icon);
  const preload = rest.preload && index < tilesPerRow * (slide && !mosaic?.enabled ? 1 : 3);

  if (numbered && !isNewSLView) {
    return (
      <NumberedGameTile
        className={styles['fixedWidth' + upperFirst(icon)]}
        count={count}
        game={game}
        race={race}
        icon={icon}
        index={index}
        fsbLaunch={fsbLaunch}
        onLaunch={onLaunch}
        preload={preload}
        favouriteIcon={favouriteIcon}
      />
    );
  } else if (isNewSLView || rest.jackpotWidget) {
    return (
      <CasinoGameTileSL
        count={count}
        game={game}
        icon={icon}
        index={index}
        fsbLaunch={fsbLaunch}
        onLaunch={onLaunch}
        preload={preload}
        favouriteIcon={favouriteIcon}
        liveRaceIcon={false}
        noIconScale={noIconScale}
        className={className}
        mosaicHint={mosaicHint}
        gameDeckMosaic={rest.gameDeckMosaic}
        jackpotClassName={jackpotClassName}
        overlayHierarchy={rest.overlayHierarchy}
        numbered={numbered}
        disableIcons={disableIcons}
        disableProviderLogos={disableProviderLogos}
        gameDeck={gameDeck}
        isMosaic={mosaic?.enabled}
      />
    );
  } else if (isLive(icon)) {
    return (
      <LiveCasinoGameTile
        game={game}
        grid={!slide}
        onLaunch={onLaunch}
        favouriteIcon={favouriteIcon}
      />
    );
  } else {
    return (
      <CasinoGameTile
        count={count}
        game={game}
        race={race}
        icon={icon}
        index={index}
        fsbLaunch={fsbLaunch}
        onLaunch={onLaunch}
        preload={preload}
        favouriteIcon={favouriteIcon}
        liveRaceIcon={false}
        noIconScale={noIconScale}
        className={className}
        mosaicHint={mosaicHint}
        gameDeckMosaic={rest.gameDeckMosaic}
        jackpotClassName={jackpotClassName}
        gameCategory={rest.gameCategory}
      />
    );
  }
};

const CasinoMetaTile: React.FC<Props> = ({ game, index, styles = {}, ...rest }) => {
  const mosaicProps = useMemo(() => {
    const props = getMosaicProps(rest.mosaic, styles, rest.gameDeckMosaic, rest.isNewSLView);
    return props[index % props.length];
  }, [rest.mosaic, rest.gameDeckMosaic, rest.isNewSLView, styles, index]);

  if (isGame(game)) {
    return <GameBasedTile game={game} index={index} styles={styles} {...rest} {...mosaicProps} />;
  } else if (isPromo(game)) {
    const castPromo = game as Promo;
    return <CasinoPromoTile index={index} promo={castPromo} {...rest} {...mosaicProps} />;
  }
  return null;
};

export default React.memo(CasinoMetaTile);
